import type { Organization } from '@viamrobotics/web-client/app/v1/app';
import type { Timestamp } from '@viamrobotics/web-client/google/protobuf/timestamp';

import { getMostRecentOrgID } from './get-most-recent-org';

export interface InitializeOrgResult {
  orgID: string | undefined;
  /** True when the requested org ID is not in the user's accessible orgs list. */
  orgNotAccessible: boolean;
}

/**
 * Initialize the current org from either query params, session storage or
 * the most recent org accessed by the user, in that order of priority.
 *
 * `orgNotAccessible` is only returned for an org id that was explicitly
 * requested via the `?org=` URL parameter. A stale session storage value or
 * stale most-recent entry silently falls back to the first accessible org so
 * that internal navigations don't redirect a user to an error page based on
 * server-side state they didn't ask for.
 */
export const initializeDefaultOrgID = (
  orgs: Organization[] | undefined,
  orgLastAccess: Record<string, Timestamp> | undefined
): InitializeOrgResult => {
  const urlOrgID = getOrgIDFromURLParams();
  const sessionOrgID = getSessionStorageOrgID();
  const isOrgInAccessibleList = (id: string | undefined): boolean =>
    id !== undefined && (orgs?.some((org) => org.id === id) ?? false);

  // An explicit URL request to an inaccessible org is the only case that
  // surfaces the orgNotAccessible page. The caller (root layout) is
  // responsible for the redirect.
  if (
    urlOrgID !== undefined &&
    orgs !== undefined &&
    !isOrgInAccessibleList(urlOrgID)
  ) {
    sessionStorage.removeItem('currentOrgId');
    return { orgID: urlOrgID, orgNotAccessible: true };
  }

  if (urlOrgID !== undefined) {
    setBrowsersSessionOrgID(urlOrgID);
    return { orgID: urlOrgID, orgNotAccessible: false };
  }

  // From here on the URL did not request a specific org. Prefer the session
  // storage value if it's still accessible, otherwise silently fall back to
  // the most recently accessed (and still accessible) org, otherwise the
  // first accessible org.
  if (sessionOrgID !== undefined) {
    // If we don't yet know which orgs are accessible, trust the session
    // value to avoid clobbering it on transient prefetch failures.
    if (orgs === undefined || isOrgInAccessibleList(sessionOrgID)) {
      setBrowsersSessionOrgID(sessionOrgID);
      return { orgID: sessionOrgID, orgNotAccessible: false };
    }

    // Session storage value is stale. Drop it before picking a fallback so
    // we never persist an inaccessible org id.
    sessionStorage.removeItem('currentOrgId');
  }

  if (orgs?.[0]) {
    const mostRecentOrgID = getMostRecentOrgID(orgLastAccess);
    const fallbackOrgID = isOrgInAccessibleList(mostRecentOrgID)
      ? (mostRecentOrgID ?? orgs[0].id)
      : orgs[0].id;
    setBrowsersSessionOrgID(fallbackOrgID);
    return { orgID: fallbackOrgID, orgNotAccessible: false };
  }

  return { orgID: undefined, orgNotAccessible: false };
};

const getSessionStorageOrgID = (): string | undefined => {
  return sessionStorage.getItem('currentOrgId') ?? undefined;
};

export const getOrgIDFromURLParams = (): string | undefined => {
  const urlParams = new URLSearchParams(window.location.search);
  const orgIDParam = urlParams.get('org');
  return orgIDParam ?? undefined;
};

export const setOrgIDInURLParams = (id: string): void => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set('org', id);
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}?${urlParams.toString()}`
  );
};

export const getBrowserSessionOrgID = (): string | undefined => {
  const orgIdParam = getOrgIDFromURLParams();
  if (orgIdParam) {
    return orgIdParam;
  }
  return sessionStorage.getItem('currentOrgId') ?? undefined;
};

/** Set the persisted default org ID. */
export const setBrowsersSessionOrgID = (id: string): string => {
  // set url params
  setOrgIDInURLParams(id);
  // set session storage
  sessionStorage.setItem('currentOrgId', id);
  return id;
};

/** Clear the persisted default org ID from session storage and URL params. */
export const clearBrowserSessionOrgID = (): void => {
  sessionStorage.removeItem('currentOrgId');
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete('org');
  const search = urlParams.toString();
  window.history.replaceState(
    window.history.state,
    '',
    search ? `${window.location.pathname}?${search}` : window.location.pathname
  );
};
